import { useMemo, useState } from 'react'
import { Alert, Space, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PrinterOutlined, RollbackOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { fetchReceiptPage, type Receipt, type ReceiptFilter } from './cashdeskApi'
import { ReceiptReverseModal } from './ReceiptReverseModal'
import { useReceiptPrinter } from './useReceiptPrinter'

interface ReceiptTableProps {
  filter: ReceiptFilter
  tableId: string
  urlPrefix: string
}

export function ReceiptTable({ filter, tableId, urlPrefix }: ReceiptTableProps) {
  const { t } = useTranslation()
  const printer = useReceiptPrinter()
  const { print, printingId } = printer
  const [reversing, setReversing] = useState<Receipt | null>(null)
  const { subscriberId, branchId, mine, day } = filter
  const stableFilter = useMemo<ReceiptFilter>(
    () => ({ subscriberId, branchId, mine, day }),
    [subscriberId, branchId, mine, day],
  )
  const fetchPage = useMemo(() => fetchReceiptPage(stableFilter), [stableFilter])

  const columns = useMemo<ColumnsType<Receipt>>(
    () => [
      {
        key: 'createdAt',
        title: t('cashdesk.columns.date'),
        width: 170,
        render: (_, row) => <span title={utcTooltip(row.createdAt)}>{formatDateTime(row.createdAt)}</span>,
      },
      {
        key: 'receiptNo',
        title: t('cashdesk.columns.receiptNo'),
        width: 190,
        render: (_, row) => (
          <Space size={4} wrap>
            <Typography.Text>{row.receiptNo}</Typography.Text>
            {row.kind === 'REVERSAL' ? <Tag color="orange">{t('cashdesk.receiptKind.REVERSAL')}</Tag> : null}
            {row.status === 'PARTIAL' ? <Tag color="gold">{t('cashdesk.receiptStatus.PARTIAL')}</Tag> : null}
            {row.reversedBy ? <Tag>{t('cashdesk.reversedBy', { no: row.reversedBy.receiptNo })}</Tag> : null}
            {row.reversesReceiptNo ? (
              <Tag>{t('cashdesk.reversesReceipt', { no: row.reversesReceiptNo })}</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        key: 'amount',
        title: t('cashdesk.columns.amount'),
        width: 140,
        align: 'right',
        render: (_, row) => formatMoneyValue(row.amount),
      },
      {
        key: 'customer',
        title: t('cashdesk.columns.customer'),
        width: 160,
        render: (_, row) => row.customer.login,
      },
      {
        key: 'branch',
        title: t('cashdesk.columns.branch'),
        width: 160,
        render: (_, row) => row.branch.name,
      },
      {
        key: 'cashier',
        title: t('cashdesk.columns.cashier'),
        width: 160,
        render: (_, row) => row.cashier.name,
      },
      {
        key: 'actions',
        title: t('common.actions'),
        width: 64,
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'reprint',
                label: t('cashdesk.reprint'),
                icon: <PrinterOutlined />,
                loading: printingId === row.id,
                onClick: () => void print(row),
              },
              {
                key: 'reverse',
                label: t('cashdesk.reverse.action'),
                icon: <RollbackOutlined />,
                permission: 'cash.receipt.reverse',
                danger: true,
                divider: true,
                hidden: row.kind !== 'PAYMENT' || row.reversedBy !== null,
                onClick: () => setReversing(row),
              },
            ]}
          />
        ),
      },
    ],
    [t, print, printingId],
  )

  return (
    <>
      {printer.error ? (
        <Alert
          type="error"
          showIcon
          closable
          onClose={printer.clearError}
          style={{ marginBottom: 12 }}
          message={translateError(printer.error, t)}
        />
      ) : null}
      <DataTable<Receipt>
        queryKey={[...queryKeys.cashdesk.receipts, tableId, stableFilter]}
        fetchPage={fetchPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId={tableId}
        urlPrefix={urlPrefix}
        scrollX={1100}
        showTotal={false}
        emptyTitle={t('cashdesk.receiptsEmpty')}
      />
      <ReceiptReverseModal
        receipt={reversing}
        onClose={() => setReversing(null)}
        onReversed={(reversal) => {
          setReversing(null)
          void printer.print(reversal)
        }}
      />
      {printer.portal}
    </>
  )
}
